/**
 * 真人出镜模型 / 时长 / 分辨率档位表(0915 老板拍板:「放开模型档+时长档+分辨率档」,车 C)。
 *
 * ═══ 为什么单独抽一个模块 ═══════════════════════════════════════════════════════
 * `VideoRealCreatePage` 此前「无模型选择」(设计定稿④),`REAL_PERSON_MODEL` 只是个
 * 借 fast 价签的报价占位常量,ai 侧实际执行钉死 vapeur 满血 2.0——UI 和真实执行从没对齐过。
 * 老板拍板放开后,档位表 + 计费 model_code 映射是**跨组件都要用的知识**(页面渲染 chips、
 * 报价 quoteParams、提交 buildTaskRequest 三处都要一份逐字相同的口径),抽出来才能单测,
 * 不然只能靠渲染整页面断言字符串。
 *
 * ═══ 内部模型 id vs 计费 model_code:两个不同的名字空间 ═══════════════════════════
 * request.model 字段发的是**内部模型 id**(`vapeur-seedance-2-0` 这种,vapeur 中转层认的名字,
 * provider 字段维持 "seedance" 不变);报价 `variantByModel(sku, modelCode, suffix)` 第二参要的
 * 是**计费 model_code**(`doubao-seedance-2-0-rp-novin` 这种,`xb_price_sku_variant.model_code`
 * 那一列)——两者不是同一个字符串,不能互相替代。`realPersonBillingModelCode` 就是这道换算。
 *
 * ═══ 2.0 的 1080p 特例 ═══════════════════════════════════════════════════════════
 * 契约只给 2.0 在「无视频输入(novin)+ 1080p」这一格单列了 model_code
 * (`doubao-seedance-2-0-rp-1080p-novin`)——其余分辨率、其余模型的 vin/novin 各一个
 * model_code,不再按分辨率二次拆分。若后续价目表要给别的模型也开 1080p 专档,
 * 在这里加一条 if 分支,不要改调用方。
 */
import { engineLabel } from "@/shared/brand/engineTiers";
import type { VideoResolution } from "../api/videoService";
import { identityRefBillsAsVideoInput, type IdentityRefMode } from "./identityRefMode";

export type RealPersonModelId =
  | "vapeur-seedance-2-0"
  | "vapeur-seedance-2-5"
  | "vapeur-seedance-2-0-fast"
  | "vapeur-seedance-2-0-mini";

export interface RealPersonModelTier {
  value: RealPersonModelId;
  /**
   * 简称(消息文案/toast 拼句用,如「专业」「旗舰」)—— 不写英文 id。
   * ★OEM 0929(C-C):走 engineLabel 档位名(专业 / 旗舰 / 极速 / 轻量),超管非代客态附真名。
   */
  label: string;
  /** chip 上完整展示的文案(名字+一句话卖点,如「满血 2.0·推荐」)。 */
  chipLabel: string;
  /** chip title 悬浮提示,补一句 chipLabel 装不下的细节。 */
  desc: string;
  /** 单次直发(本页两种玩法都不经编排)的时长上限,秒。 */
  maxDurationSec: number;
  /** 支持的分辨率档集合 —— 决定分辨率 chip 是否可选。 */
  resolutions: readonly VideoResolution[];
}

/**
 * 四档,顺序即 UI 展示顺序。能力口径(前端钳位,api 侧同集校验):
 *   2-0  = 480p/720p/1080p + ≤15s(默认档)
 *   2-5  = 480p/720p/1080p + ≤30s(最长、有 1080p)
 *   fast = 480p/720p       + ≤15s(无 1080p)
 *   mini = 480p/720p       + ≤15s(无 1080p)
 */
export const REAL_PERSON_MODEL_TIERS: readonly RealPersonModelTier[] = [
  {
    value: "vapeur-seedance-2-0",
    get label() {
      return engineLabel("realPerson", null, "vapeur-seedance-2-0");
    },
    chipLabel: "专业·推荐",
    desc: "效果稳定均衡,拿不准选哪档就选它",
    maxDurationSec: 15,
    resolutions: ["480p", "720p", "1080p"],
  },
  {
    value: "vapeur-seedance-2-5",
    get label() {
      return engineLabel("realPerson", null, "vapeur-seedance-2-5");
    },
    chipLabel: "旗舰·画质最强·可拍30秒",
    desc: "画质最强,唯一能拍到 30 秒的档",
    maxDurationSec: 30,
    resolutions: ["480p", "720p", "1080p"],
  },
  {
    value: "vapeur-seedance-2-0-fast",
    get label() {
      return engineLabel("realPerson", null, "vapeur-seedance-2-0-fast");
    },
    chipLabel: "极速·更省",
    desc: "出片更快、积分更省,不支持 1080p",
    maxDurationSec: 15,
    resolutions: ["480p", "720p"],
  },
  {
    value: "vapeur-seedance-2-0-mini",
    get label() {
      return engineLabel("realPerson", null, "vapeur-seedance-2-0-mini");
    },
    chipLabel: "轻量·最省",
    desc: "积分开销最低,不支持 1080p",
    maxDurationSec: 15,
    resolutions: ["480p", "720p"],
  },
] as const;

export const DEFAULT_REAL_PERSON_MODEL: RealPersonModelId = "vapeur-seedance-2-0";
/** 真人出镜页初始时长(秒)—— 页面 initialForm 与 hub 卡面价签共用这一份(B2 #9)。 */
export const REAL_PERSON_DEFAULT_DURATION_SEC = 15;

/** 未知/空 id 一律落默认档(满血 2.0)——不让一个查不到的 id 让下游拿 undefined 崩。 */
export function realPersonModelTier(id: string | null | undefined): RealPersonModelTier {
  return (
    REAL_PERSON_MODEL_TIERS.find((t) => t.value === id) ??
    REAL_PERSON_MODEL_TIERS[0] /* vapeur-seedance-2-0 */
  );
}

export function supportsResolution(model: RealPersonModelId, resolution: VideoResolution): boolean {
  return realPersonModelTier(model).resolutions.includes(resolution);
}

/**
 * 报价 `variantByModel(sku, modelCode, suffix)` 的第二参 —— 计费 model_code。
 * 逐字对齐契约表(2026-09-15 车 C):
 *   2.0  → doubao-seedance-2-0-rp-vin / -novin(novin+1080p 专档见下)
 *   2.5  → doubao-seedance-2-5-rp-vin / -novin
 *   fast → doubao-seedance-2-0-fast-rp-vin / -novin
 *   mini → doubao-seedance-2-0-mini-rp-vin / -novin
 */
export function realPersonBillingModelCode(
  model: RealPersonModelId,
  resolution: VideoResolution,
  hasVideoInput: boolean
): string {
  const vin = hasVideoInput ? "vin" : "novin";
  if (model === "vapeur-seedance-2-0") {
    if (!hasVideoInput && resolution === "1080p") return "doubao-seedance-2-0-rp-1080p-novin";
    return `doubao-seedance-2-0-rp-${vin}`;
  }
  if (model === "vapeur-seedance-2-5") return `doubao-seedance-2-5-rp-${vin}`;
  if (model === "vapeur-seedance-2-0-fast") return `doubao-seedance-2-0-fast-rp-${vin}`;
  return `doubao-seedance-2-0-mini-rp-${vin}`; // vapeur-seedance-2-0-mini
}

/**
 * 报价 `variantByModel` 的第三参(suffix)。★与 `PriceBook.variantByModel` 的匹配语义配套 ——
 * 那边拿 `suffix.toUpperCase()` 去比 variantKey 的**结尾**(整串相等,或以 `_${suffix}` 收尾),
 * 所以这里给的必须是「分辨率_vin|novin」这个后缀本身,不能再拼 model 名前缀
 * (前缀是价目表 seed 那边的知识,如 `seedance20_rp_` / `seedance25_rp_`,前端不猜)。
 */
export function realPersonVariantSuffix(
  resolution: VideoResolution,
  hasVideoInput: boolean
): string {
  return `${resolution}_${hasVideoInput ? "vin" : "novin"}`;
}

/** 换模型时分辨率是否要跟着回落(fast/mini 不支持 1080p)。 */
export function clampResolutionForModel(
  resolution: VideoResolution,
  model: RealPersonModelId
): VideoResolution {
  return supportsResolution(model, resolution) ? resolution : "720p";
}

/** 换模型时时长是否要跟着下钳(切到上限更低的档时)。 */
export function clampDurationForModel(durationSec: number, model: RealPersonModelId): number {
  return Math.min(durationSec, realPersonModelTier(model).maxDurationSec);
}

/**
 * 身份视频缺失实测秒数时的报价回落值(0915 车 E)—— vapeur 视频素材硬上限 15 秒,
 * 宁可轻微高报也不漏计量。见 {@link realPersonQuoteMetrics} 头注。
 */
export const IDENTITY_VIDEO_FALLBACK_SEC = 15;

export interface RealPersonQuoteMetricsInput {
  mode: "text" | "swap";
  model: RealPersonModelId;
  resolution: VideoResolution;
  /** 文案出片时长档(仅 text 模式用于 qty)。 */
  durationSec: number;
  /** 换我出镜参考视频实测秒(仅 swap 模式用于 qty;未测出前为 null)。 */
  swapDurationSec: number | null;
  /**
   * 这一单选的身份参考档(仅 text 模式决定 vin/novin;swap 模式恒 vin,不读这个字段)。
   *
   * ★0920 从布尔 `hasVideoAvatar` 升成三态:此前是「人物有没有挂视频 ∧ 用户选了带视频」
   * 两个判据在调用方合成一个布尔喂进来,第三档(仅身份视频)出来后那种合成表达不了 ——
   * 直接收档位,判据只此一处,调用方不再自己拼。
   */
  identityRefMode: IdentityRefMode;
  /** 该人物身份视频实测秒(仅 text 模式且档位带视频时读;NULL/undefined 回落 15)。 */
  identityVideoDurationSec?: number | null;
}

export interface RealPersonQuoteMetrics {
  /** 是否按带视频输入(vin)计费 —— 与 realPersonBillingModelCode/realPersonVariantSuffix 入参一致。 */
  hasVideoInput: boolean;
  /** `variantByModel` 第二参。 */
  modelCode: string;
  /** `variantByModel` 第三参。 */
  variantSuffix: string;
  qty: number;
}

/**
 * 报价计量口径(0915 车 E,vin 族报价修正方案 a)。★只动 quote 计量,提交 payload 一个字不动——
 * 提交侧仍是 `mode === "swap"` 判 hasVideoInput、`durationSec`/`swapDurationSec` 各走各的字段,
 * 这个函数只喂 `quoteParams`。
 *
 * ═══ 补的两处计量不足 ══════════════════════════════════════════════════════════
 * ① 文案出片带上身份视频时(0920 起 = `identityRefMode` 为 photo_video / video):
 *   → vapeur 侧走 vin 计费码(输入视频 token 全计量),报价此前恒选 novin、qty 只算
 *   出片秒 —— 报价锁实扣下亏本卖。这里 vin/novin 的选择跟**档位**走,qty 在
 *   出片秒之外加上身份视频秒数(`identityVideoDurationSec` 缺失时按硬上限 15s 回落,
 *   宁可轻微高报);photo 档维持现状(novin,qty=出片秒),零回归。
 * ② 换我出镜:qty 此前 = 参考视频秒,但 vin 形态 token = (输入 + 输出) × 每秒率,
 *   输入 ≈ 输出的参考视频体量,要算 2 倍。换我出镜恒 vin,不读 `identityRefMode`
 *   (提交必带参考视频,与人物挂了什么身份素材无关)。
 *
 * 两种模式互斥:调用方两组字段都传,这里按 `mode` 分流,不需要自己先判断该读哪些字段。
 * 时长还没就绪时返回 `null`(text 的 `durationSec<=0` 或 swap 的 `swapDurationSec` 为
 * `null`/`<=0`)—— 与既有「查不到价就不显示估算」同一条纪律,调用方不猜一个数。
 */
export function realPersonQuoteMetrics(
  input: RealPersonQuoteMetricsInput
): RealPersonQuoteMetrics | null {
  const {
    mode,
    model,
    resolution,
    durationSec,
    swapDurationSec,
    identityRefMode,
    identityVideoDurationSec,
  } = input;

  if (mode === "swap") {
    if (swapDurationSec == null || swapDurationSec <= 0) return null;
    const hasVideoInput = true; // 换我出镜恒 vin(提交必带参考视频)。
    return {
      hasVideoInput,
      modelCode: realPersonBillingModelCode(model, resolution, hasVideoInput),
      variantSuffix: realPersonVariantSuffix(resolution, hasVideoInput),
      qty: 2 * swapDurationSec, // 输入≈输出,vin token 双倍计量。
    };
  }

  if (durationSec <= 0) return null;
  // ★三档只有两种计量:带视频(photo_video / video)= vin + 加身份视频秒,不带 = novin。
  //   仅视频与「定妆照＋身份视频」**同价同量** —— 少送一张图不会让价格变低
  //   (vapeur 只要收到视频输入就走 vin 计费码,qty 公式一个字没改)。
  const hasVideoInput = identityRefBillsAsVideoInput(identityRefMode);
  const identitySec = hasVideoInput
    ? Math.ceil(identityVideoDurationSec ?? IDENTITY_VIDEO_FALLBACK_SEC)
    : 0;
  return {
    hasVideoInput,
    modelCode: realPersonBillingModelCode(model, resolution, hasVideoInput),
    variantSuffix: realPersonVariantSuffix(resolution, hasVideoInput),
    qty: durationSec + identitySec,
  };
}
